import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseAmount, type GroupMemberResponse } from "@qota/shared";
import { Amount, Avatar, Button, Icon, Sheet } from "@qota/ui";
import { createSettlement, getBalances } from "../lib/balances";
import { ApiRequestError } from "../lib/http";
import { queryKeys } from "../lib/query-keys";

const euro = (cents: number) => (cents / 100).toFixed(2).replace(".", ",") + " €";

/**
 * Schermata 08 — Salda i conti.
 *
 * Due modi di registrare un pagamento, e il secondo è quello che conta.
 *
 * I trasferimenti suggeriti si confermano con un tocco, ma spesso **non
 * corrispondono a quello che le persone fanno davvero**: con la semplificazione
 * dei debiti attiva Qota può suggerire «Ugo → Giulia», mentre Ugo ha consegnato
 * i contanti a Bonnie perché era lì. Per questo esiste il modulo libero, dove
 * si sceglie chi paga, chi riceve e quanto.
 *
 * Toccare un suggerimento precompila il modulo: è così che si registra un
 * pagamento parziale — un tocco e si corregge l'importo.
 */
export function SettleSheet({
  groupId,
  members,
  open,
  onClose,
  withMemberId = null,
  myMemberId = null,
}: {
  groupId: string;
  members: GroupMemberResponse[];
  open: boolean;
  onClose: () => void;
  /**
   * La controparte da cui si è arrivati, quando il foglio si apre dal dettaglio
   * di un conto invece che dal pulsante del gruppo.
   */
  withMemberId?: string | null;
  /** Chi sta guardando: l'altra metà del pagamento già scelto. */
  myMemberId?: string | null;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const [amountText, setAmountText] = useState("");

  const { data } = useQuery({
    queryKey: queryKeys.balances(groupId),
    queryFn: () => getBalances(groupId),
    enabled: open,
  });

  // Chiudendo il foglio il modulo torna vuoto: riaprirlo con dentro i resti
  // della volta scorsa è il modo più facile di registrare un pagamento
  // sbagliato senza accorgersene.
  useEffect(() => {
    if (open) return;
    setFromId(null);
    setToId(null);
    setAmountText("");
    setError(null);
  }, [open]);

  /**
   * Arrivando dal dettaglio di un conto, il pagamento è già scelto: manca solo
   * l'importo.
   *
   * Il verso si ricava dal saldo della controparte — se è in credito riceve,
   * altrimenti versa — invece di essere passato da chi apre il foglio: così non
   * c'è modo che il chiamante lo indichi al contrario.
   */
  useEffect(() => {
    if (!open || !withMemberId || !myMemberId || !data) return;

    const altro = data.balances.find((entry) => entry.memberId === withMemberId);
    if (!altro) return;

    if (altro.balanceCents > 0) {
      setFromId(myMemberId);
      setToId(withMemberId);
    } else {
      setFromId(withMemberId);
      setToId(myMemberId);
    }
  }, [open, withMemberId, myMemberId, data]);

  const mutation = useMutation({
    mutationFn: (input: { fromMemberId: string; toMemberId: string; amountCents: number }) =>
      createSettlement(groupId, { ...input, currency: data?.currency ?? "EUR" }),
    onSuccess: async () => {
      // I saldi si ricalcolano dai movimenti: basta invalidare, non c'è nessuno
      // stato da aggiornare a mano.
      await queryClient.invalidateQueries({ queryKey: queryKeys.balances(groupId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.settlements(groupId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.timeline(groupId) });

      setAmountText("");
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Registrazione del pagamento non riuscita.",
      );
    },
  });

  const transfers = data?.transfers ?? [];
  const amount = parseAmount(amountText || "0", "EUR");
  const amountCents = amount?.cents ?? 0;

  const nameOf = (memberId: string) =>
    members.find((member) => member.id === memberId)?.displayName ?? "?";

  /**
   * Il saldo di chi paga dopo questo versamento.
   * Se diventa positivo, chi doveva diventa creditore — è corretto (magari si
   * arrotonda per eccesso) ma è anche un modo facile di sbagliare a digitare,
   * quindi si dice invece di bloccarlo.
   */
  const payerBalance = fromId
    ? (data?.balances.find((entry) => entry.memberId === fromId)?.balanceCents ?? 0)
    : 0;
  const afterPaying = payerBalance + amountCents;
  const overshoot = fromId && amountCents > 0 && afterPaying > 0 ? afterPaying : 0;

  const canSubmit =
    fromId !== null && toId !== null && fromId !== toId && amountCents > 0 && !mutation.isPending;

  function prefillFrom(transfer: (typeof transfers)[number]) {
    setFromId(transfer.fromMemberId);
    setToId(transfer.toMemberId);
    setAmountText((transfer.amountCents / 100).toFixed(2).replace(".", ","));
    setError(null);
  }

  return (
    <Sheet
      open={open}
      title="Salda i conti"
      subtitle={
        transfers.length > 0
          ? `Con ${transfers.length} ${transfers.length === 1 ? "trasferimento" : "trasferimenti"} tornate in pari`
          : undefined
      }
      size="tall"
      onClose={onClose}
      footer={
        <Button variant="secondary" size="lg" full onClick={onClose}>
          Chiudi
        </Button>
      }
    >
      {transfers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
          <span style={{ color: "var(--money-positive)", display: "inline-flex" }}>
            <Icon name="circle-check" size={28} />
          </span>
          <p style={{ fontSize: 14, fontWeight: 600, marginTop: 10 }}>Siete in pari</p>
        </div>
      ) : (
        transfers.map((transfer, index) => (
          <div
            key={`${transfer.fromMemberId}-${transfer.toMemberId}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 0",
              borderTop: index === 0 ? "none" : "1px solid var(--border-subtle)",
            }}
          >
            <Avatar name={transfer.fromDisplayName} size={32} />
            <Icon name="arrow-right" size={15} />
            <Avatar name={transfer.toDisplayName} size={32} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                {transfer.fromDisplayName} → {transfer.toDisplayName}
              </div>
              <div style={{ marginTop: 2 }}>
                <Amount cents={transfer.amountCents} size={13} tone="neutral" />
              </div>
            </div>

            <Button
              variant="secondary"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => prefillFrom(transfer)}
            >
              Registra
            </Button>
          </div>
        ))
      )}

      <div
        style={{
          marginTop: transfers.length > 0 ? "var(--space-5)" : "var(--space-3)",
          paddingTop: "var(--space-4)",
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            fontSize: "var(--text-label-size)",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--text-secondary)",
            marginBottom: 4,
          }}
        >
          Registra un pagamento
        </div>
        <p
          style={{
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--text-secondary)",
            margin: "0 0 14px",
            textWrap: "pretty",
          }}
        >
          Qualunque importo, anche parziale, e fra due persone qualsiasi — non deve corrispondere a
          un suggerimento.
        </p>

        <MemberPicker
          label="Chi paga"
          members={members}
          selectedId={fromId}
          onSelect={(id) => {
            setFromId(id);
            setError(null);
          }}
        />

        <MemberPicker
          label="Chi riceve"
          members={members}
          selectedId={toId}
          disabledId={fromId}
          onSelect={(id) => {
            setToId(id);
            setError(null);
          }}
          style={{ marginTop: 14 }}
        />

        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--text-secondary)",
              marginBottom: 6,
            }}
          >
            Importo
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 42,
              padding: "0 13px",
              borderRadius: "var(--radius-sm)",
              background: "var(--white)",
              border: "1px solid var(--border-strong)",
            }}
          >
            <input
              inputMode="decimal"
              placeholder="0,00"
              value={amountText}
              aria-label="Importo del pagamento"
              onChange={(event) => {
                setAmountText(event.target.value);
                setError(null);
              }}
              style={{
                flex: 1,
                minWidth: 0,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: "var(--font-numeric)",
                fontSize: "var(--input-font-size, 16px)",
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-tertiary)" }}>€</span>
          </div>
        </div>

        {overshoot > 0 && (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 10,
              padding: "10px 12px",
              background: "var(--status-warning-bg)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <span style={{ color: "var(--status-warning)", display: "flex", paddingTop: 1 }}>
              <Icon name="info" size={16} />
            </span>
            <div style={{ fontSize: 12, lineHeight: "18px", textWrap: "pretty" }}>
              Con questo importo {fromId ? nameOf(fromId) : "chi paga"} diventa creditore di{" "}
              <strong>{euro(overshoot)}</strong>.
            </div>
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{ fontSize: 12.5, fontWeight: 700, color: "var(--coral-500)", marginTop: 10 }}
          >
            {error}
          </div>
        )}

        <Button
          variant="accent"
          size="lg"
          full
          style={{ marginTop: 16 }}
          disabled={!canSubmit}
          onClick={() =>
            fromId &&
            toId &&
            mutation.mutate({ fromMemberId: fromId, toMemberId: toId, amountCents })
          }
        >
          {mutation.isPending ? "Registro…" : "Registra il pagamento"}
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * Selezione di una persona con pastiglie orizzontali.
 *
 * Non un `select`: su mobile aprirebbe la ruota di sistema per scegliere fra
 * tre nomi, e servono due tocchi invece di uno.
 */
function MemberPicker({
  label,
  members,
  selectedId,
  disabledId,
  onSelect,
  style,
}: {
  label: string;
  members: GroupMemberResponse[];
  selectedId: string | null;
  /** Chi non si può scegliere: pagare sé stessi non sposta nulla. */
  disabledId?: string | null;
  onSelect: (memberId: string) => void;
  style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      <div
        style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}
      >
        {label}
      </div>

      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
        {members.map((member) => {
          const selected = member.id === selectedId;
          const disabled = member.id === disabledId;

          return (
            <button
              key={member.id}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onSelect(member.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                flexShrink: 0,
                height: 40,
                padding: "0 12px 0 6px",
                borderRadius: "var(--radius-pill)",
                border: "none",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.35 : 1,
                background: selected ? "var(--ink-900)" : "var(--surface-sunken)",
                color: selected ? "var(--white)" : "var(--text-primary)",
                fontFamily: "var(--font-text)",
                fontSize: 13,
                fontWeight: 700,
                transition: "background var(--duration-fast) var(--ease-standard)",
              }}
            >
              <Avatar name={member.displayName} size={28} />
              {member.displayName}
            </button>
          );
        })}
      </div>
    </div>
  );
}
